import * as React from "react";
import { History } from "history";
import { RouterStore } from "mobx-react-router";
import { Router, Switch, Route } from "react-router";
import { observer, Provider } from "mobx-react";
import SignupPage from "./pages/auth/SignupPage";
import autobind from "autobind-decorator";
import AccountService from "./service/AccountService";
import LoginPage from "./pages/auth/LoginPage";
import { CssBaseline, Radio, Typography, Button } from "@mui/material";
import AuthRoute from "./libraries/AuthRoute";
import FileListPage from "./pages/file-list/FileListPage";
import FileService from "./service/FileService";
import EditorPage from "./pages/editor/EditorPage";
import QuestionnaireChatPage from "./pages/questionnaire/QuestionnaireChatPage";
import NarrativeListPage from "./pages/narrative/NarrativeListPage";
import NarrativePage from "./pages/narrative/NarrativePage";
import AdminPage from "./pages/auth/AdminPage";
import AdminLogin from "./pages/auth/AdminLogin";
import InjectedPlanPage from "./pages/subscription/PlanPage";
import PlanSuccess from "./pages/subscription/PlanSuccess";
import PlanFail from "./pages/subscription/PlanFail";
import ProfilePage from "./pages/auth/ProfilePage";
import { styled } from "@mui/material/styles";
import styledComponents from "styled-components";
import { observable } from "mobx";
import { getPlatformType } from "./Config";
import Swipe from "react-easy-swipe";
import QuestionnaireChatbot from "./pages/questionnaire/QuestionnaireChatbot";
import WebChatHome from "./pages/questionnaire/WebChatHome";
import WebChat from "./pages/webchat/WebChat";
import QuestionnaireEmbeddedPage from "./pages/questionnaire/QuestionnaireEmbeddedPage";
import "antd/dist/antd.css";
import ContactPage from "./pages/contact/ContactPage";
import TemplatePage from "./pages/templates/TemplatesPage";
import SendService from "./service/sendService";
import ChatMessaengerService from "./service/ChatMessengerService";
import PrivateRoute from "./libraries/PrivateRoute";

const IntroTitle = styled("span")({
  fontSize: "30px",
  fontWeight: 600,
  fontFamily: "Graphik",
  letterSpacing: "0px",
  margin: "5px 20px 30px",
});

const IntroContent = styled("span")({
  fontSize: "18px",
  fontWeight: 600,
  fontFamily: "Graphik",
  letterSpacing: "0px",
  margin: "12px",
  marginTop: "24px",
  textAlign: "center",
});

const IntroContentLink = styled("a")({
  textDecoration: "none",
  fontSize: "18px",
  fontWeight: 600,
  fontFamily: "Graphik",
  letterSpacing: "0px",
  margin: "0px 5px",
  color: "#000",
  height: "fit-content",
});

const SplashScreen = styled("div")({
  position: "fixed",
  top: 0,
  width: "100%",
  height: "100%",
  backgroundImage: `url(${require("./assets/SplashScreen.png")})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
});

type ApplicationProps = {
  accountService: AccountService;
  routingService: RouterStore;
  fileService: FileService;
  history: History<any>;
  sendService: SendService;
  chatMessengerService: ChatMessaengerService;
};

type IntroImageProps = {
  src: string;
};

const IntroImage = styledComponents("div")<IntroImageProps>((props: any) => ({
  width: "262",
  height: "165",
  backgroundImage: `url(${props.src})`,
  backgroundSize: "contain",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
}));

@observer
export default class Application extends React.Component<ApplicationProps> {
  @observable
  isSplash: boolean = getPlatformType() === "MOBILE" ? true : false;

  splashTimeout = null;

  @observable
  isIntro: boolean = false;

  @observable
  selectedIntroScreen = "a";

  @autobind
  isLoggedIn(): boolean {
    return this.props.accountService.user != null;
  }

  @autobind
  isLoggedOut(): boolean {
    return this.props.accountService.user == null;
  }

  @autobind
  isAdminLoggedIn(): boolean {
    return this.props.accountService.adminUser != null;
  }

  @autobind
  isAdminLoggedOut(): boolean {
    return this.props.accountService.adminUser === null;
  }

  @autobind
  handleIntroScreenChange(event: any) {
    this.selectedIntroScreen = event.target.value;
  }

  @autobind
  handleGoClick() {
    localStorage.setItem("Intro", "DONE");
    this.isIntro = false;
  }

  renderIntro() {
    var offset = this.selectedIntroScreen == "b" ? "-100%" : 0;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          position: "absolute",
          width: "200%",
          left: offset,
          transition: "left 250ms ease-in-out",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <IntroTitle>Step 1:</IntroTitle>
          <IntroImage src={require("./assets/1Flowchart.png")} />
          <IntroContent>
            Go to
            <IntroContentLink target="_blank" href="https://FlowCharts.ai">
              https://FlowCharts.ai
            </IntroContentLink>
            to create your Flowchart, Workflow, Questionnaire, Form or Survey
          </IntroContent>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <IntroTitle>Step 2:</IntroTitle>
          <IntroImage src={require("./assets/2momandchild.png")} />
          <IntroContent>
            Then login to this app to access and administer your Flowchart,
            Workflow, Questionnaire, Form or Survey{" "}
          </IntroContent>
          <Button variant="outlined" onClick={this.handleGoClick}>
            Let's Go!
          </Button>
        </div>
      </div>
    );
  }

  renderIntroScreenBGDiv() {
    return (
      <React.Fragment>
        <Swipe
          onSwipeLeft={() => (this.selectedIntroScreen = "b")}
          onSwipeRight={() => (this.selectedIntroScreen = "a")}
        >
          <div
            style={{
              position: "fixed",
              top: 0,
              width: "100%",
              height: "100%",
              backgroundImage: "linear-gradient(#00FFFF, #00CFFE)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {this.renderIntro()}
            <div
              style={{
                position: "absolute",
                bottom: 20,
                margin: "10px 5px",
              }}
            >
              <Radio
                checked={this.selectedIntroScreen === "a"}
                onChange={this.handleIntroScreenChange}
                value="a"
                color="default"
                name="radio1"
              />
              <Radio
                checked={this.selectedIntroScreen === "b"}
                onChange={this.handleIntroScreenChange}
                value="b"
                color="default"
                name="radio2"
              />
            </div>
          </div>
        </Swipe>
      </React.Fragment>
    );
  }

  componentDidMount() {
    this.splashTimeout = setTimeout(() => {
      this.isSplash = false;
      this.isIntro = getPlatformType() === "MOBILE" ? true : false;
    }, 4000);
  }

  componentWillUnmount() {
    clearTimeout(this.splashTimeout);
  }

  render() {
    let introDone = localStorage.getItem("Intro") === "DONE" ? true : false;
    return (
      <Provider
        routing={this.props.routingService}
        account={this.props.accountService}
        files={this.props.fileService}
        chatMessenger={this.props.chatMessengerService}
        send={this.props.sendService}
      >
        <CssBaseline />
        {this.isSplash ? (
          <SplashScreen />
        ) : this.isIntro && !introDone ? (
          this.renderIntroScreenBGDiv()
        ) : (
          <Router history={this.props.history}>
            <Switch>
              <Route path="/blank" render={() => <div />} />
              <AuthRoute
                path="/signup"
                component={SignupPage}
                authCheck={this.isLoggedOut}
                redirectTo="/"
              />
              <AuthRoute
                path="/login"
                component={LoginPage}
                authCheck={this.isLoggedOut}
                redirectTo="/"
              />
              <AuthRoute
                path="/editor/:id?"
                component={EditorPage}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              />
              <PrivateRoute
                path="/chat/:userId/:clinicId/:id?/:colorSelected?"
                component={QuestionnaireChatbot}
              />

              <Route path="/web_to_sms/:userId" component={WebChatHome} />
              <Route
                path="/web_to_sms/:userId/:clinicId/:id?/:colorSelected?"
                component={WebChatHome}
              />
              <Route
                path="/webchat/:userId/:clinicId/:id?"
                component={WebChat}
              />
              <Route
                path="/questionnaire/:userId/:clinicId/:id?"
                component={QuestionnaireChatPage}
              />
              <PrivateRoute
                path="/embedded/:userId/:clinicId/:id?"
                component={QuestionnaireEmbeddedPage}
              />

              <AuthRoute
                path="/narratives/:id?"
                component={NarrativeListPage}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              />
              <AuthRoute
                path="/narrative/:id?"
                component={NarrativePage}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              />
              <AuthRoute
                path="/plan"
                component={InjectedPlanPage}
                authCheck={() => true}
                redirectTo="/login"
              />
              <AuthRoute
                path="/profile"
                component={ProfilePage}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              />
              <AuthRoute
                path="/planSuccess/:session_id?"
                component={PlanSuccess}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              ></AuthRoute>
              <AuthRoute
                path="/planFail"
                component={PlanFail}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              ></AuthRoute>
              <AuthRoute
                path="/contacts"
                component={ContactPage}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              ></AuthRoute>
              <AuthRoute
                path="/templates"
                component={TemplatePage}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              ></AuthRoute>
              <AuthRoute
                path="/adminlogin"
                component={AdminLogin}
                authCheck={this.isAdminLoggedOut}
                redirectTo="/admin"
              />
              <AuthRoute
                path="/admin"
                component={AdminPage}
                authCheck={this.isAdminLoggedIn}
                redirectTo="/adminLogin"
              />

              <AuthRoute
                path="/"
                component={FileListPage}
                authCheck={this.isLoggedIn}
                redirectTo="/login"
              />
            </Switch>
          </Router>
        )}
      </Provider>
    );
  }
}
